import { IFormProfile, NotificationGroupId, NotificationItemId, OpenFormData } from "@multiweb/types/contracts";
import { IMainMenuItemActionParams, IOpenFormModalParams } from "@multiweb/types/multiweb";
import { ReactActionId } from "@multiweb/types/react";
import { ApplicationTheme } from "@multiweb/uiShared/ApplicationTheme";
import {
    APPLY_THEME,
    CLOSE_FORM,
    EXECUTE_REACT_CONNECTOR_ACTION,
    HIDE_ALL_FORMS,
    HIDE_FORM_OVERLAY,
    HIDE_LOADING_INDICATOR,
    LOAD_FAVORITE_FORMS,
    LOAD_HISTORY_FORMS_INFO,
    LOAD_LOCAL_STORAGE_KEY,
    LOAD_LOCALIZATION_ITEMS,
    LOAD_MENU_ITEMS,
    LOAD_NOTIFICATION_GROUPS,
    LOAD_NOTIFICATION_ITEMS,
    LOAD_TASK_RESULT_DETAIL,
    LOAD_USER_INFO,
    LOGOUT,
    OPEN_FORM,
    OPEN_FORM_MODAL,
    PROCESS_MENU_ITEM_CUSTOM_ACTION,
    SELECTED_PROFILE_CHANGED,
    SET_FORM_AS_FAVORITE_REQUEST,
    SET_GROUP_NOTIFICATIONS_AS_READ,
    SET_LOCAL_STORAGE_KEY,
    SET_NOTIFICATION_ITEM_AS_READ,
    SET_REACT_CONNECTOR_MULTISELECT_MODE,
    SHOW_FORM_OVERLAY,
    SHOW_LOADING_INDICATOR,
    SHOW_SETTINGS,
    SWITCH_TO_FORM,
} from "./constants";
import {
    FormOpenMethod,
    IApplyThemeAction,
    ICloseFormAction,
    IExecuteReactConnectorActionAction,
    IHideAllFormsAction,
    IHideFormOverlayAction,
    IHideLoadingIndicatorAction,
    ILoadFavoriteFormsAction,
    ILoadHistoryFormsInfoAction,
    ILoadLocalizationItemsAction,
    ILoadLocalStorageKeyAction,
    ILoadMenuItemsAction,
    ILoadNotificationGroupsAction,
    ILoadNotificationItemsAction,
    ILoadTaskResultDetailAction,
    ILoadUserInfoAction,
    ILogoutAction,
    IOpenFormAction,
    IOpenFormModalAction,
    IProcessMenuItemCustomActionAction,
    ISelectedProfileChangedAction,
    ISetFormAsFavoriteRequestAction,
    ISetGroupNotificationsAsReadAction,
    ISetLocalStorageKeyAction,
    ISetNotificationItemAsReadAction,
    ISetReactConnectorMultiselectModeAction,
    IShowFormOverlayAction,
    IShowLoadingIndicatorAction,
    IShowSettingsAction,
    ISwitchToFormAction,
} from "./types";

/**
 * Akce vyvolávající načtení dat o přihlášeném uživateli.
 */
export const createLoadUserInfoAction = (): ILoadUserInfoAction => ({
    type: LOAD_USER_INFO,
});

/**
 * Akce vyvolávající načtení položek pro menu.
 */
export const createLoadMenuItemsAction = (): ILoadMenuItemsAction => ({
    type: LOAD_MENU_ITEMS,
});

/**
 * Akce vyvolávající načtení položek lokalizace pro zadané klíče.
 * @param locKeys Klíče, pro které mají být načteny lokalizační položky.
 * @param key Klíč pro jednoznačnou identifikaci požadavku. Umožňuje rozlišit více akcí tohoto typu z různých zdrojů.
 */
export const createLoadLocalizationItemsAction = (locKeys: readonly string[], key: string): ILoadLocalizationItemsAction => ({
    type: LOAD_LOCALIZATION_ITEMS,
    payload: {
        key,
        locKeys,
    },
});

/**
 * Akce zobrazující načítací indikátor. Akce může být volána vícekrát. Načítací indikátor je schován až když je zavolaný odpovídající počet akcí pro skrytí.
 */
export const createShowLoadingIndicatorAction = (): IShowLoadingIndicatorAction => ({
    type: SHOW_LOADING_INDICATOR,
});

/**
 * Akce skrývající načítací indikátor. Akce může být volána vícekrát. Indikátor je skryt až když je akce pro schování zavolána tolikrát, kolikrát byla akce pro zobrazení
 * a pokud neprovádí načítání původní část aplikace.
 */
export const createHideLoadingIndicatorAction = (): IHideLoadingIndicatorAction => ({
    type: HIDE_LOADING_INDICATOR,
});

/**
 * Akce pro otevření zadaného formulář.
 * @param formCode Kód formuláře k otevření.
 * @param openMethod Metoda otevření. Určuje, zda je možné využít existující formulář, nebo se vždy otevře nový.
 * @param openFormData Volitelná data využívaná při otevírání formuláře.
 */
export const createOpenFormAction = (formCode: string, openMethod: FormOpenMethod, openFormData: OpenFormData): IOpenFormAction => ({
    type: OPEN_FORM,
    payload: {
        formCode,
        openMethod,
        openFormData,
    },
});

/**
 * Akce indikující změnu vybraného profilu formuláře
 * @param profile Změněný profil
 */
export const createSelectedProfileChangedAction = (formInstanceId: string, profile?: IFormProfile): ISelectedProfileChangedAction => ({
    type: SELECTED_PROFILE_CHANGED,
    payload: {
        formInstanceId,
        profile,
    },
});

/**
 * Akce pro otevření zadaného modálního okna.
 * @param formName Název formuláře k otevření.
 * @param attributeValues Seznam názvu attributů, jejichž hodnota se má získat.
 * @param onFormClose Callback volaný při zavření formuláře.
 * @param condition Podmínka pro otevíraný formulář.
 * @param conditionParamsObject Instance aplikačního záznamu, ze kterého se načítají hodnoty parametrů podmínky
 * @param conditionParamsAttrs Názvy aplikačních atributů, v nichž jsou hodnoty k parametrům podmínky
 * @param masterLinkToAttr Název vazby na nadřizeny objekt
 */
export const createOpenFormModalAction = (params: IOpenFormModalParams): IOpenFormModalAction => ({
    type: OPEN_FORM_MODAL,
    payload: {
        params,
    },
});

/**
 * Akce pro odhlášení uživatele.
 */
export const createLogoutAction = (): ILogoutAction => ({
    type: LOGOUT,
});

/**
 * Akce pro zobrazení dialogu s nastavením.
 */
export const createShowSettingsAction = (): IShowSettingsAction => ({
    type: SHOW_SETTINGS,
});

/**
 * Akce pro zobrazení overlaye nad formulářovou částí. Pokud je již overlay zobrazen, nemá žádný účinek.
 */
export const createShowFormOverlayAction = (): IShowFormOverlayAction => ({
    type: SHOW_FORM_OVERLAY,
});

/**
 * Akce pro skrytí overlaye nad formulářovou částí. Pokud je overlay již skrytý, nemá žádný účinek. Na rozdíl od indikátoru načítání,
 * je jedno kolikrát byla zavolána akce pro zobrazení, akce pro schování overlay vždy schová.
 */
export const createHideFormOverlayAction = (): IHideFormOverlayAction => ({
    type: HIDE_FORM_OVERLAY,
});

/**
 * Akce pro načtení oblíbených formulářů. Po načtení vrací výsledek pomocí akce FAVORITE_FORMS_LOADED.
 */
export const createLoadFavoriteFormsAction = (): ILoadFavoriteFormsAction => ({
    type: LOAD_FAVORITE_FORMS,
});

/**
 * Akce spouštící proces pro přepnutí oblíbenosti formuláře.
 * @param menuId ID položky menu.
 * @param formCode Kód formuláře k přepnutí.
 * @param isFavorite Hodnota, na kterou se má přepnout. True pro nastavení formuláře jako oblíbeného. False pro jeho odebrání z oblíbených.
 * @param profile Profil formuláře.
 */
export const createSetFormAsFavoriteRequestAction = (menuId: number, formCode: string, isFavorite: boolean, profile?: IFormProfile): ISetFormAsFavoriteRequestAction => ({
    type: SET_FORM_AS_FAVORITE_REQUEST,
    payload: {
        menuId,
        formCode,
        isFavorite,
        profile,
    },
});

/**
 * Akce pro načtení notifikačních položek.
 * @param notificationGroupId Identifkátor notifikační skupiny notifikačních položek
 * @param startIndex Počáteční index načítaných notifikačních položek
 * @param itemsCount Počet načítaných notifikačních položek
 */
export const createLoadNotificationItemsAction = (notificationGroupId: NotificationGroupId, startIndex: number, itemsCount: number): ILoadNotificationItemsAction => ({
    type: LOAD_NOTIFICATION_ITEMS,
    payload: {
        notificationGroupId,
        startIndex,
        itemsCount,
    },
});

/**
 * Akce pro načítání notifikačních skupin.
 */
export const createLoadNotificationGroupsAction = (): ILoadNotificationGroupsAction => ({
    type: LOAD_NOTIFICATION_GROUPS,
});

/**
 * Akce pro označení notifikační položky za přečtenou/nepřečtenou.
 * @param itemId Identifikátor notifikační položky
 * @param isRead Příznak, zda má být notifikační položka označena za přečtenou či nepřečtenou.
 */
export const createSetNotificationItemAsReadAction = (itemId: NotificationItemId, isRead: boolean): ISetNotificationItemAsReadAction => ({
    type: SET_NOTIFICATION_ITEM_AS_READ,
    payload: {
        itemId,
        isRead,
    },
});
/**
 * Akce pro označení všech notifikací dané skupiny za přečtené.
 * @param notificationGroupId Identifikátor skupiny
 * @param panelItemId Identifikátor panelu
 */
export const createSetGroupNotificationsAsReadAction = (notificationGroupId: NotificationGroupId, panelItemId: string): ISetGroupNotificationsAsReadAction => ({
    type: SET_GROUP_NOTIFICATIONS_AS_READ,
    payload: {
        notificationGroupId,
        panelItemId,
    },
});

/**
 * Akce pro načtení detailu o výsledku úlohy.
 * @param objId Identifikátor otevíraného objektu.
 */
export const createLoadTaskResultDetailAction = (objId: number): ILoadTaskResultDetailAction => ({
    type: LOAD_TASK_RESULT_DETAIL,
    payload: {
        objId,
    },
});

/**
 * Akce pro načtení historie otevřených formulářů. Po načtení vrací výsledek pomocí akce HISTORY_FORMS_INFO_UPDATED.
 */
export const createLoadHistoryFormsInfoAction = (): ILoadHistoryFormsInfoAction => ({
    type: LOAD_HISTORY_FORMS_INFO,
});

/**
 * Akce pro zavření formuláře.
 *
 * @param formInstanceId Informace identifikující otevřený formulář, který chceme zavřít.
 */
export const createCloseFormAction = (formInstanceId: string): ICloseFormAction => ({
    type: CLOSE_FORM,
    payload: {
        formInstanceId,
    },
});

/**
 * Akce pro skrytí otevřených formulářů.
 */
export const createHideAllFormsAction = (): IHideAllFormsAction => ({
    type: HIDE_ALL_FORMS,
});

/**
 * Akce pro přepnutí otevřeného formuláře do popředí.
 * @param formInstanceId Id otevřené instance formuláře.
 */
export const createSwitchToFormAction = (formInstanceId: string): ISwitchToFormAction => ({
    type: SWITCH_TO_FORM,
    payload: {
        formInstanceId,
    },
});

/**
 * Akce pro aplikaci tématu aplikace.
 * @param theme Název tématu které se má nastavit.
 */
export const createApplyThemeAction = (theme: ApplicationTheme): IApplyThemeAction => ({
    type: APPLY_THEME,
    payload: {
        theme,
    },
});

/**
 * Akce pro nastavení hodnoty klíče v local storage.
 * @param key Klíč.
 * @param value Hodnota která se má nastavit.
 */
export const createSetLocalStorageKeyAction = (key: string, value: string): ISetLocalStorageKeyAction => ({
    type: SET_LOCAL_STORAGE_KEY,
    payload: {
        key,
        value,
    },
});

/**
 * Akce pro načtení hodnoty klíče z local storage.
 * @param key Klíč.
 */
export const createLoadLocalStorageKeyAction = (key: string): ILoadLocalStorageKeyAction => ({
    type: LOAD_LOCAL_STORAGE_KEY,
    payload: {
        key,
    },
});

/**
 * Akce pro spuštění zpracování custom akce pro položku menu.
 * @param menuItemParams Údaje o položce menu, pro níž se má akce zpracovat.
 */
export const createProcessMenuItemCustomActionAction = (menuItemParams: IMainMenuItemActionParams): IProcessMenuItemCustomActionAction => ({
    type: PROCESS_MENU_ITEM_CUSTOM_ACTION,
    payload: {
        menuItemParams,
    },
});

/**
 * Akce pro zavolání metody execute na akci v connectoru.
 *
 * @param dataSourceKey Identifikátor connectoru.
 * @param actionId Identifikátor akce na connectoru.
 * @param dataObjectId Identifikátor záznamu, který způsobil zavolání metody exexute.
 */
export const createExecuteReactConnectorAction = (dataSourceKey: string, actionId: ReactActionId, dataObjectId: string): IExecuteReactConnectorActionAction => ({
    type: EXECUTE_REACT_CONNECTOR_ACTION,
    payload: {
        dataSourceKey,
        actionId,
        dataObjectId,
    },
});

/**
 * Akce pro nastavení MultiselectMode v connectoru.
 * Musí se aktualizovat při každé změně MultiselectMode, aby měli akce v toolbaru
 * správnou viditelnost (toolbar není react komponenta, proto nemůže reagovat na změnu ve store).
 *
 * @param dataSourceKey Identifikátor connectoru.
 * @param multiselectMode Aktuální hodnota MultiselectMode.
 */
export const createSetReactConnectorMultiselectModeAction = (dataSourceKey: string, multiselectMode: boolean): ISetReactConnectorMultiselectModeAction => ({
    type: SET_REACT_CONNECTOR_MULTISELECT_MODE,
    payload: {
        dataSourceKey,
        multiselectMode,
    },
});
